import { createClient, type SupabaseClient } from "@supabase/supabase-js";

const SERVICE_ROLE_ERROR =
  "[supabase] VITE_SUPABASE_PUBLISHABLE_KEY must be the publishable (anon) key, " +
  "not the service_role key. The service_role key must stay server-side only. " +
  "RLS is the security boundary.";

/**
 * Detects a service_role key by two methods:
 *   1. Literal string match (catches obvious copy-paste mistakes).
 *   2. JWT payload decode: the real Supabase service_role JWT carries
 *      `{ "role": "service_role" }` in its base64url payload.
 */
function isServiceRoleKey(key: string): boolean {
  if (key.includes("service_role")) return true;
  try {
    const parts = key.split(".");
    if (parts.length !== 3) return false;
    // base64url → standard base64 (restore padding so atob never throws)
    const seg = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const b64 = seg + "==".slice((seg.length + 3) % 4);
    const json = atob(b64);
    const payload = JSON.parse(json) as Record<string, unknown>;
    return payload?.role === "service_role";
  } catch {
    return false;
  }
}

/**
 * Creates a Supabase browser client using the publishable (anon) key from env.
 *
 * Guards (enforced at call-time, not build-time):
 *   - VITE_SUPABASE_URL must be set and non-empty
 *   - VITE_SUPABASE_PUBLISHABLE_KEY must be set, non-empty, and must NOT be
 *     the service_role key — the service_role key stays server-side only
 *
 * RLS is the security boundary.  This client never holds elevated privileges.
 * No @supabase/ssr — the portal is a static SPA (Vite + Cloudflare Pages).
 */
export function createBrowserClient(): SupabaseClient {
  const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as
    | string
    | undefined;

  if (!url || url.trim() === "") {
    throw new Error(
      "[supabase] VITE_SUPABASE_URL is required. " +
        "Copy apps/portal/.env.example to apps/portal/.env and fill in your project URL.",
    );
  }
  if (!key || key.trim() === "") {
    throw new Error(
      "[supabase] VITE_SUPABASE_PUBLISHABLE_KEY is required. " +
        "Copy apps/portal/.env.example to apps/portal/.env and fill in the publishable (anon) key.",
    );
  }
  if (isServiceRoleKey(key)) {
    throw new Error(SERVICE_ROLE_ERROR);
  }

  return createClient(url, key, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
    },
  });
}

let _client: SupabaseClient | null = null;

/** Returns the singleton browser client, creating it on first call. */
export function getSupabaseClient(): SupabaseClient {
  if (!_client) {
    _client = createBrowserClient();
  }
  return _client;
}

/** Resets the cached singleton — for unit tests only. */
export function _resetClientForTest(): void {
  _client = null;
}
